import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateHRAExemption, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const HRACalculator = () => {
  const [basicSalary, setBasicSalary] = useState(50000);
  const [da, setDa] = useState(5000);
  const [hraReceived, setHraReceived] = useState(20000);
  const [rentPaid, setRentPaid] = useState(25000);
  const [isMetro, setIsMetro] = useState(true);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [basicSalary, da, hraReceived, rentPaid, isMetro]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const bs = Number(basicSalary) || 0;
    const d = Number(da) || 0;
    const hra = Number(hraReceived) || 0;
    const rent = Number(rentPaid) || 0;
    const hraResult = calculateHRAExemption(bs, d, hra, rent, isMetro);
    setResult(hraResult);
  };

  const handleDownloadPDF = () => {
    generateGenericPDF('HRA Exemption Report', {
      'Basic Salary (Monthly)': basicSalary,
      'Dearness Allowance (Monthly)': da,
      'HRA Received (Monthly)': hraReceived,
      'Rent Paid (Monthly)': rentPaid,
      'City Type': isMetro ? 'Metro' : 'Non-Metro',
      'Annual HRA Received': result.condition1,
      'Rent - 10% of Salary': result.condition2,
      [`${isMetro ? '50' : '40'}% of Basic + DA`]: result.condition3,
      'HRA Exemption (Annual)': result.exemption,
      'Taxable HRA (Annual)': result.taxableHRA,
    });
  };

  const handleReset = () => {
    setBasicSalary(50000);
    setDa(5000);
    setHraReceived(20000);
    setRentPaid(25000);
    setIsMetro(true);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="HRA Calculator"
        description="Free HRA Exemption Calculator India - Calculate House Rent Allowance tax exemption under Section 10(13A)."
        keywords="HRA calculator, HRA exemption calculator, house rent allowance calculator, HRA tax exemption, Section 10(13A) calculator"
        canonicalPath="/hra"
      />
      <div className="calculator-header">
        <h1>HRA Calculator</h1>
        <p>Calculate House Rent Allowance exemption under Section 10(13A)</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Basic Salary (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={basicSalary === '' ? '' : basicSalary}
                onChange={(e) => setBasicSalary(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
                max="1000000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Dearness Allowance (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={da === '' ? '' : da}
                onChange={(e) => setDa(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="500000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>HRA Received (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={hraReceived === '' ? '' : hraReceived}
                onChange={(e) => setHraReceived(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="500000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Rent Paid (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={rentPaid === '' ? '' : rentPaid}
                onChange={(e) => setRentPaid(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="500000"
              />
            </div>
          </div>

          <div className="input-group">
            <label>City Type</label>
            <div className="flex-row" style={{ marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={isMetro}
                  onChange={() => setIsMetro(true)}
                />
                Metro (Delhi, Mumbai, Chennai, Kolkata)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={!isMetro}
                  onChange={() => setIsMetro(false)}
                />
                Non-Metro
              </label>
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>HRA Exemption (Annual)</h3>
                <div className="amount">{formatCurrency(result.exemption)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Taxable HRA (Annual)</span>
                  <span className="value" style={{ color: '#ef4444' }}>
                    {formatCurrency(result.taxableHRA)}
                  </span>
                </div>
              </div>

              <div className="breakup-section">
                <h4>HRA Exemption Calculation (Minimum of 3 conditions)</h4>
                <table className="schedule-table">
                  <thead>
                    <tr>
                      <th>Condition</th>
                      <th>Amount (Annual)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: result.exemption === result.condition1 ? '#dcfce7' : '' }}>
                      <td>1. Actual HRA Received</td>
                      <td>{formatCurrency(result.condition1)}</td>
                    </tr>
                    <tr style={{ background: result.exemption === result.condition2 ? '#dcfce7' : '' }}>
                      <td>2. Rent Paid - 10% of (Basic + DA)</td>
                      <td>{formatCurrency(result.condition2)}</td>
                    </tr>
                    <tr style={{ background: result.exemption === result.condition3 ? '#dcfce7' : '' }}>
                      <td>3. {isMetro ? '50%' : '40%'} of (Basic + DA)</td>
                      <td>{formatCurrency(result.condition3)}</td>
                    </tr>
                  </tbody>
                </table>
                <p style={{ marginTop: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  ✓ The highlighted row shows the minimum value which is the exempted amount.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About HRA Exemption</h4>
        <ul>
          <li><strong>Eligibility:</strong> Salaried employees receiving HRA as part of salary</li>
          <li><strong>Metro Cities:</strong> Delhi, Mumbai, Chennai, Kolkata (50% of Basic+DA)</li>
          <li><strong>Non-Metro:</strong> All other cities (40% of Basic+DA)</li>
          <li><strong>Required Documents:</strong> Rent receipts, landlord's PAN (if rent {'>'}₹1 lakh/year)</li>
          <li><strong>No HRA?</strong> You can claim deduction under Section 80GG instead</li>
        </ul>
      </div>
    </div>
  );
};

export default HRACalculator;
